/**
 * Anmeldung.
 *
 * Zweispaltig wie im Design: links das Eichenherz-Logo und die Personen-Auswahl,
 * rechts die Wortmarke KORA mit dem Claim. Der zweite Schritt ist kein
 * Passwortfeld mehr, sondern die Weiterleitung zur Microsoft-Anmeldung – das
 * Passwort gehört zum Microsoft-Konto.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import type { LoginTeam, Nutzer } from '../typen';
import './login.css';

export function Login() {
  const { nachAnmeldung } = useKora();
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const abfrage = useQuery({
    queryKey: ['login-team'],
    queryFn: () => api.hole<LoginTeam>('/auth/team'),
    retry: 1,
  });

  const demoModus = abfrage.data?.modus === 'demo';
  const team = abfrage.data?.team ?? [];

  async function anmelden(name: string) {
    setGewaehlt(name);
    setFehler('');

    if (!demoModus) {
      // Microsoft übernimmt: eigene Seite, deshalb harte Weiterleitung.
      window.location.assign(`/api/auth/login?name=${encodeURIComponent(name)}`);
      return;
    }

    setLaeuft(true);
    try {
      const nutzer = await api.sende<Nutzer>('/auth/demo', { name });
      nachAnmeldung(nutzer);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : 'Die Anmeldung ist fehlgeschlagen.');
      setGewaehlt(null);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="login">
      <div className="login-marke">
        <img
          src="/eichenherz-logo.png"
          alt="Eichenherz – Weil es Ihr Zuhause ist"
          className="login-logo"
        />
        <div className="login-claim">
          <div className="login-wortmarke">KORA</div>
          <p>Eichenherz’ zentrales Werkzeug für Kunden, Angebote und Zahlen.</p>
        </div>
        <div className="login-fuss">Internes Werkzeug der Eichenherz GmbH</div>
      </div>

      <div className="login-formular">
        <div className="login-block">
          <div>
            <div className="eh-kicker">Anmelden</div>
            <div className="login-frage">Wer arbeitet gerade?</div>
          </div>

          {abfrage.isLoading && <div className="eh-hinweis">Team wird geladen …</div>}

          {abfrage.isError && (
            <div className="eh-banner eh-banner--rot">
              Die Anwendung ist gerade nicht erreichbar. Bitte die Seite neu laden.
            </div>
          )}

          <div className="login-liste">
            {team.map((person) => (
              <button
                key={person.name}
                type="button"
                className="login-person"
                onClick={() => void anmelden(person.name)}
                disabled={laeuft}
              >
                <span
                  className="eh-avatar login-avatar"
                  style={
                    person.profilbild
                      ? { backgroundImage: `url(${person.profilbild})` }
                      : undefined
                  }
                >
                  {person.profilbild ? '' : person.initialen}
                </span>
                <span className="login-name">{person.name}</span>
                <span className="login-chevron">
                  {gewaehlt === person.name && laeuft ? '…' : '›'}
                </span>
              </button>
            ))}
          </div>

          {fehler && <div className="eh-fehler">{fehler}</div>}

          {gewaehlt && !demoModus && (
            <div className="eh-hinweis">Weiterleitung zur Microsoft-Anmeldung …</div>
          )}

          <div className="login-hinweis">
            {demoModus ? (
              <>
                Demo-Modus: die Auswahl meldet direkt an, ohne Microsoft-Konto. Für den Echtbetrieb
                in der Konfiguration <code>AUTH_MODE=entra</code> setzen.
              </>
            ) : (
              <>
                Die Anmeldung läuft über das Microsoft-Konto von Eichenherz. Zugänge verwaltet die
                Geschäftsführung im Admin Center.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
